import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  fetchActiveWorkflows,
  fetchWorkflowDetail,
  isWaitingApproval,
} from '../api/activeWorkflowsApi';
import { useAuth } from './useAuth';

const ActiveWorkflowsContext = createContext(null);

const POLL_ACTIVE_MS = 5000;
const POLL_IDLE_MS = 30000;
const RECENT_COMPLETED_TTL_MS = 5 * 60 * 1000;

// Workflow shape we expose internally (from /api/v1/workflows/history):
// { workflow_id, task_description, role, status, started_at, completed_at, needs_input }
function summarize(rawList, needsInputMap) {
  return rawList.map((w) => ({
    ...w,
    needs_input: w.status === 'paused' && needsInputMap.get(w.workflow_id) === true,
  }));
}

function diffTransitions(prev, next) {
  // Detect status transitions and new "needs_input" arrivals.
  const prevById = new Map(prev.map((w) => [w.workflow_id, w]));
  const events = [];

  for (const w of next) {
    const old = prevById.get(w.workflow_id);
    if (!old) {
      // First time we've seen this flow. We only emit an event if it's already
      // in a "needs input" state; brand new running flows don't deserve a toast.
      if (w.needs_input) {
        events.push({ type: 'needs_input', workflow: w });
      }
      continue;
    }
    if (old.status !== w.status) {
      if (w.status === 'completed') events.push({ type: 'completed', workflow: w });
      else if (w.status === 'failed') events.push({ type: 'failed', workflow: w });
      else if (w.status === 'paused' && w.needs_input)
        events.push({ type: 'needs_input', workflow: w });
    } else if (!old.needs_input && w.needs_input) {
      events.push({ type: 'needs_input', workflow: w });
    }
  }

  return events;
}

export function ActiveWorkflowsProvider({ children }) {
  const { isAuthenticated, activated } = useAuth();
  // Backend services answer 403 account_not_activated to un-activated tokens,
  // so only poll once the account is both signed in and activated.
  const isAuthed = !!isAuthenticated && !!activated;

  const [active, setActive] = useState([]); // running + paused, with needs_input
  const [recentlyCompleted, setRecentlyCompleted] = useState([]); // last completed/failed within TTL, not acknowledged
  const [acknowledgedIds, setAcknowledgedIds] = useState(() => new Set());

  const subscribersRef = useRef(new Set());
  const previousRef = useRef([]);
  const pollTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const completedSeenRef = useRef(new Map()); // workflow_id -> { workflow, seenAt }

  const subscribe = useCallback((cb) => {
    subscribersRef.current.add(cb);
    return () => subscribersRef.current.delete(cb);
  }, []);

  const emit = useCallback((events) => {
    if (!events.length) return;
    for (const cb of subscribersRef.current) {
      try {
        cb(events);
      } catch (err) {
        console.error('ActiveWorkflows subscriber error', err);
      }
    }
  }, []);

  const acknowledge = useCallback((workflowId) => {
    setAcknowledgedIds((prev) => {
      if (prev.has(workflowId)) return prev;
      const next = new Set(prev);
      next.add(workflowId);
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthed || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const raw = await fetchActiveWorkflows();

      // For paused flows, fetch detail so we can surface "needs input".
      // Cap concurrency implicitly — paused flows are usually 0–1 per user.
      const pausedIds = raw
        .filter((w) => w.status === 'paused')
        .map((w) => w.workflow_id);
      const needsInputMap = new Map();
      if (pausedIds.length) {
        const details = await Promise.all(
          pausedIds.map((id) =>
            fetchWorkflowDetail(id).catch(() => null)
          )
        );
        pausedIds.forEach((id, idx) => {
          needsInputMap.set(id, isWaitingApproval(details[idx]));
        });
      }

      const summarized = summarize(raw, needsInputMap);

      // Detect transitions vs previous active set + previous recently-completed.
      const prev = previousRef.current;
      const events = diffTransitions(prev, summarized);

      // Anything that vanished from the active list is now finished. Pull its
      // current state via a single history call filtered by completed/failed
      // — but that's expensive every cycle. Cheaper: compare prev active to
      // current active; anything in prev that's gone, we don't yet know its
      // outcome, so we treat it as "completed" optimistically and emit. If
      // a subsequent poll says otherwise, we correct.
      const currentIds = new Set(summarized.map((w) => w.workflow_id));
      for (const old of prev) {
        if (!currentIds.has(old.workflow_id)) {
          // It dropped out of active — fetch detail to learn final status.
          // Lazy enrichment: we'll mark recently-completed via the detail call.
          fetchWorkflowDetail(old.workflow_id)
            .then((detail) => {
              if (!detail) return;
              const status = detail.status || detail.workflow?.status;
              if (status === 'completed' || status === 'failed') {
                const finalised = { ...old, status, completed_at: detail.completed_at };
                completedSeenRef.current.set(old.workflow_id, {
                  workflow: finalised,
                  seenAt: Date.now(),
                });
                pruneCompleted();
                setRecentlyCompleted(buildRecentList(completedSeenRef.current, acknowledgedIds));
                emit([{ type: status, workflow: finalised }]);
              }
            })
            .catch(() => {});
        }
      }

      previousRef.current = summarized;
      setActive(summarized);
      emit(events);
    } catch (err) {
      console.error('ActiveWorkflows poll failed', err);
    } finally {
      inFlightRef.current = false;
    }
  }, [isAuthed, acknowledgedIds, emit]);

  // Helpers reading the latest acknowledgedIds via closure each call.
  function pruneCompleted() {
    const cutoff = Date.now() - RECENT_COMPLETED_TTL_MS;
    for (const [id, entry] of completedSeenRef.current) {
      if (entry.seenAt < cutoff) completedSeenRef.current.delete(id);
    }
  }
  function buildRecentList(map, acked) {
    return [...map.values()]
      .filter((e) => !acked.has(e.workflow.workflow_id))
      .map((e) => e.workflow);
  }

  // Reschedule polling whenever auth state or active count changes.
  useEffect(() => {
    if (!isAuthed) return undefined;
    refresh();
    const interval = active.length > 0 ? POLL_ACTIVE_MS : POLL_IDLE_MS;
    pollTimerRef.current = setInterval(refresh, interval);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [isAuthed, active.length, refresh]);

  // onfocus catch-up: refresh immediately when the tab regains focus.
  useEffect(() => {
    if (!isAuthed) return undefined;
    const onFocus = () => {
      refresh();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthed, refresh]);

  // Recompute the visible recently-completed list whenever acknowledgements change.
  useEffect(() => {
    setRecentlyCompleted(buildRecentList(completedSeenRef.current, acknowledgedIds));
  }, [acknowledgedIds]);

  const value = {
    running: active.filter((w) => w.status === 'running'),
    paused: active.filter((w) => w.status === 'paused'),
    needsInput: active.filter((w) => w.needs_input),
    recentlyCompleted,
    all: [...active, ...recentlyCompleted],
    refresh,
    subscribe,
    acknowledge,
  };

  return (
    <ActiveWorkflowsContext.Provider value={value}>
      {children}
    </ActiveWorkflowsContext.Provider>
  );
}

export function useActiveWorkflows() {
  const ctx = useContext(ActiveWorkflowsContext);
  if (!ctx) {
    // Safe default so consumers don't crash if the provider isn't mounted.
    return {
      running: [],
      paused: [],
      needsInput: [],
      recentlyCompleted: [],
      all: [],
      refresh: () => {},
      subscribe: () => () => {},
      acknowledge: () => {},
    };
  }
  return ctx;
}
